import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import FileUploadField from '../base/FileUploadField';
import { useGuideBuilderGuide } from '../../contexts/GuideBuilderContext';
import { useUpdateStep } from '../../hooks/useGuides';
import { AcceptedFileType, FileUploadTabType } from '../base/file-upload/utils';
import { FileMetadata } from '@/services/apis/files';
import { GuideStepType } from '@/services/apis/guides';
import StepEditorWrapper from './StepEditorWrapper';

const PdfStepEditorForm: React.FC = () => {
  const {
    guide: { id: guideId },
    state: { selectedStep },
  } = useGuideBuilderGuide();
  const { t } = useTranslation('admin');
  const updateStep = useUpdateStep(guideId || '');
  const [fileMetadata, setFileMetadata] = useState<FileMetadata | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [shouldAutoSave, setShouldAutoSave] = useState(false);

  // Initialize fileMetadata from step content
  useEffect(() => {
    const content = selectedStep?.content as any;
    if (content?.pdf && typeof content.pdf === 'object' && 'id' in content.pdf) {
      setFileMetadata(content.pdf as FileMetadata);
    } else {
      setFileMetadata(null);
    }
    setFile(null);
    setShouldAutoSave(false);
  }, [selectedStep]);

  const handleFileUploaded = (metadata: FileMetadata) => {
    setFileMetadata(metadata);
    setFile(null);
    // Trigger auto-save after file upload
    setShouldAutoSave(true);
  };

  const handleRemove = () => {
    setFileMetadata(null);
    setFile(null);
  };

  const handleSave = async () => {
    if (!selectedStep?.id || !fileMetadata) return;

    try {
      await updateStep.mutateAsync({
        stepId: selectedStep.id,
        data: {
          content: {
            pdf: fileMetadata,
          },
        },
      });
    } catch (error) {
      console.error('Error saving PDF step:', error);
    }
  };

  const handleAutoSaveComplete = useCallback(() => {
    setShouldAutoSave(false);
  }, []);

  if (!selectedStep) {
    return null;
  }

  return (
    <StepEditorWrapper
      title={t('guides.builder.stepEditor.pdfStep') || 'PDF Step'}
      stepType={GuideStepType.PDF}
      onSave={handleSave}
      isSaveDisabled={!fileMetadata || updateStep.isPending}
      isPending={updateStep.isPending}
      shouldAutoSave={shouldAutoSave}
      onAutoSaveComplete={handleAutoSaveComplete}
    >
      {fileMetadata ? (
        <div className="space-y-4">
          <div className="relative">
            <iframe
              src={fileMetadata.url}
              className="w-full h-96 border border-gray-300 dark:border-gray-600 rounded-lg"
              title={fileMetadata.name}
            />
            <Button
              isIconOnly
              color="danger"
              variant="solid"
              size="sm"
              className="absolute top-2 right-2"
              onPress={handleRemove}
              aria-label={t('common.remove') || 'Remove PDF'}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-sm text-foreground font-medium mb-1">{fileMetadata.name}</p>
            <p className="text-xs text-muted-foreground">
              {fileMetadata.type} •{' '}
              {fileMetadata.size > 0 ? `${(fileMetadata.size / 1024 / 1024).toFixed(2)} MB` : 'External URL'}
            </p>
            <a
              href={fileMetadata.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:underline text-sm mt-2 block"
            >
              {t('guides.builder.stepEditor.openInNewTab') || 'Open in new tab'}
            </a>
          </div>
        </div>
      ) : (
        <FileUploadField
          file={file}
          onChange={setFile}
          acceptedTypes={[AcceptedFileType.PDF]}
          shownTabs={[FileUploadTabType.FILE_UPLOAD, FileUploadTabType.EXTERNAL_URL, FileUploadTabType.MEDIA_LIBRARY]}
          onFileUploaded={handleFileUploaded}
          maxSize={50}
        />
      )}
    </StepEditorWrapper>
  );
};

export default PdfStepEditorForm;
